"use client"

import { KeyInsight } from "../../components/key-insight"
import { TrilogyCard } from "./trilogy-card"

export function HolistikaContent() {
  return (
    <article className="prose-container">
      {/* Title Section */}
      <header className="mb-16">
        <p className="text-sm font-medium text-accent-secondary uppercase tracking-wider mb-4">
          Business Manifiesto
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 text-balance">
          Holistika
        </h1>
        <p className="text-xl sm:text-2xl text-muted-foreground leading-relaxed max-w-3xl">
          A Framework for Business Logic and Growth: Because Management Is a Technology
        </p>
        <div className="flex items-center gap-4 mt-8 pt-8 border-t border-border">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Reading time:</span> 9 min
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Version:</span> 2.9
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Last updated:</span> 2026
          </div>
        </div>
      </header>

      {/* Section 1: Core Philosophy */}
      <section id="core-philosophy" className="scroll-mt-40 mb-20">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-primary">01</span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">
            Core Philosophy: The Primacy of Logic
          </h2>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p>
            At the heart of Holistika lies a fundamental belief: <strong>efficiency in any endeavor is contingent upon a deep understanding of the underlying logic that governs it.</strong> We seek out and apply logical frameworks from a multitude of sources—including the social sciences—to our primary objective: enhancing business performance.
          </p>

          <KeyInsight>
            Companies are human constructs. Human behavior, and the logic that underpins it, is as relevant to a business as it is to any other aspect of human life. This is why we validate the application of social sciences to comprehend corporate dynamics.
          </KeyInsight>
          
          <p>
            Consider hierarchy. As any collaborative human effort grows in scale and complexity, the need for a consensually agreed-upon external direction becomes paramount. This principle holds true for families, governments, and corporations alike. In the corporate context, this responsibility ultimately rests with the board of directors. This dynamic is a microcosm of leadership structures found throughout an organization.
          </p>

          <p>
            Most enterprises originate from a product, with the primary goal of value exchange. Initially, the company is little more than a product and the capabilities required to manage it. However, success necessitates scaling—both in product innovation and in the expansion of management capabilities. At a certain point in this growth trajectory, a true company emerges: <em>a synergistic set of capabilities adept at value creation, with expertise in managing a specific product.</em>
          </p>

          <div className="my-10 p-6 bg-card rounded-xl border border-border">
            <h4 className="text-base font-semibold text-foreground mb-4">The Founder Transition Problem</h4>
            <p className="text-muted-foreground leading-relaxed">
              A common pitfall we observe is the failure of companies to successfully transition from a product-centric entity to a fully-fledged enterprise. This is particularly evident in the technology sector, where innovative products demand deep disciplinary expertise. In the early stages, leadership roles are often filled by individuals with strong technical backgrounds—essentially, a future CTO acting as CEO. As the company grows, a shift in leadership requirements occurs. Sometimes the founder acquires business management skills; other times, a new CEO is brought in.
            </p>
          </div>

          <p>
            This observation was the catalyst for Holistika&apos;s development. We recognized that a successful product inevitably leads to the creation of a company where <strong>management, not the product itself, becomes the key to sustained success.</strong> This begs the question: why not begin with a focus on the company?
          </p>

          <p>
            All companies adhere to a remarkably similar structural model; specialization dictates variations at the lower levels of the organizational chart. However, the principles of administration and management remain largely consistent. Therefore, Holistika embarked on creating a structure of administrative and management capabilities, grounded in a universal logic of companies, that could be applied independently of any specific product or sector.
          </p>
        </div>
      </section>

      {/* Section 2: The Methodological Trinity */}
      <section id="methodological-trinity" className="scroll-mt-40 mb-12">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-primary">02</span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">
            The Methodological Trinity
          </h2>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground mb-10">
          <p>
            Holistika&apos;s operational framework rests on three interconnected pillars. Each represents a distinct level of abstraction, yet all three must work in harmony to drive sustainable business success.
          </p>
        </div>

        {/* Trinity Cards */}
        <div className="grid gap-6 md:grid-cols-3 mb-10">
          <TrilogyCard
            number="S"
            title="Strategy"
            subtitle="Direction toward success"
            description="Open-ended objectives born from future aspirations and capability understanding"
            color="amber"
          />
          <TrilogyCard
            number="T"
            title="Tactics"
            subtitle="Building blocks"
            description="Finite objectives that break strategy into manageable, measurable components"
            color="teal"
          />
          <TrilogyCard
            number="P"
            title="Processes"
            subtitle="Execution engine"
            description="The granular steps and procedures that transform tactics into reality"
            color="slate"
          />
        </div>
      </section>

      {/* Section 2a: Strategy */}
      <section id="strategy" className="scroll-mt-40 mb-16 ml-0 md:ml-8">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-secondary">02a</span>
          <h3 className="text-xl sm:text-2xl font-semibold text-foreground">
            Strategy: The Unending Pursuit of Success
          </h3>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p>
            The primary responsibility of a company&apos;s management is the stewardship of its most fundamental need: strategy. A business, by its very nature, requires direction.
          </p>

          <KeyInsight>
            Strategy is a series of open-ended objectives, born from the company&apos;s future aspirations and an understanding of its capabilities, aimed at establishing a direction toward success.
          </KeyInsight>

          <p>
            This definition, however, proved initially too ambiguous in practice. We encountered &ldquo;strategies&rdquo; that were finite and targeted specific aspects of the business rather than its overall success—often executed by individuals lacking a holistic view of the company&apos;s welfare. This realization led to identifying the next essential layer.
          </p>
        </div>
      </section>

      {/* Section 2b: Tactics */}
      <section id="tactics" className="scroll-mt-40 mb-16 ml-0 md:ml-8">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-secondary">02b</span>
          <h3 className="text-xl sm:text-2xl font-semibold text-foreground">
            Tactics: The Building Blocks of Strategy
          </h3>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p>
            To achieve a broad strategy, it must be broken down into smaller, finite objectives. The execution of these tactics requires meticulous management and the development of limited resources and capabilities.
          </p>

          <p>
            To this end, Holistika sought out and integrated various frameworks: <strong>PMI, Agile methodologies, DAMA, and political and industry-specific leadership frameworks.</strong> At their core, these frameworks provide a structured and optimized organization of concepts to achieve a beneficial goal. They identify and conceptualize human needs within specific sectors or activities, creating a relational map that enables users to perform their tasks more effectively.
          </p>

          <div className="my-10 p-6 bg-card rounded-xl border border-border">
            <h4 className="text-base font-semibold text-foreground mb-4">The Value of Standardized Frameworks</h4>
            <p className="text-muted-foreground leading-relaxed">
              The adoption of standardized processes helps mitigate the inherent biases in human decision-making in abstract domains. This foundation provided Holistika with a robust management framework: a comprehensive definition of strategy, vital capabilities across all necessary management areas, and a suite of multidisciplinary, proven frameworks to ensure proper control of any business activity.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2c: Processes */}
      <section id="processes" className="scroll-mt-40 mb-20 ml-0 md:ml-8">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-secondary">02c</span>
          <h3 className="text-xl sm:text-2xl font-semibold text-foreground">
            Processes: The Execution of Tactics
          </h3>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p>
            Direction and a management framework alone are insufficient. The next level of necessity for the successful implementation of strategy and tactics is <strong>processes</strong>.
          </p>

          <p>
            Holistika has always distinguished itself from the idea of being a specialist. While the market places high value on specialized companies and profiles—often conflating specialization with expertise—we have pursued a different path. We sought a logical capability that was not tied to any specific case but could be applied across many.
          </p>

          <KeyInsight>
            Fifteen years of development have been dedicated to the complete understanding and conceptualization of the logic that governs companies. The goal: to understand the capabilities that create specialists in all areas, without focusing exclusively on low-level processes.
          </KeyInsight>

          <p>
            Companies do not need profound technical knowledge of all their capabilities; rather, they require complete understanding and management capacity to enable specialized profiles to fill specific needs. This is what we offer: <strong>knowledge and control.</strong>
          </p>

          <p>
            The knowledge must be completely holistic. This transversal logic across the entire business landscape provides control recommended by best practices in all areas of business, updated to the current state of the art—be it AI, Data Governance, RPA, process management, service or project management, digital marketing, or the generation of new business lines.
          </p>

          <div className="my-10 p-6 bg-amber-600/5 rounded-xl border border-amber-600/20">
            <h4 className="text-base font-semibold text-foreground mb-4">On Foresight</h4>
            <p className="text-muted-foreground leading-relaxed">
              The product that shines is the ideal strategy that comes from management authority. But what should truly shine is the ability to coordinate the set of steps to make it a reality. <em>Foresight is about soft skills; the rest require processes and resources.</em> Foresight is the logic by which we process all elements that globalize business and process. The ability to understand the logical basis of each element and to conceptualize is what provides vision and allows them to be combined.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: Data Governance */}
      <section id="data-governance" className="scroll-mt-40 mb-20">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-primary">03</span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">
            Data Governance and Artificial Intelligence
          </h2>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p>
            The democratization of AI prompted Holistika to investigate its potential for business, which led to our current strategic focus. Within our methodology, <strong>data governance</strong> is an area of particular interest.
          </p>

          <p>
            Technology has provided global access to objective business data, enabling companies to automatically quantify any necessary impact. This information is now available to employees who do not need to master the technical aspects of systems or data creation. Unlike in the past, the data function of a business no longer resides exclusively within IT.
          </p>

          <KeyInsight>
            Data governance is now an independent function in many companies, with a clear objective: to inform decisions. The nature of data is to provide objective and measured information about the company, upon which studied decisions—corroborated by impact shown in the data—can be made.
          </KeyInsight>

          <p>
            Holistika began by answering fundamental questions: What is the difference between methodologies like PM, DM, Agile? Why are some, like Lean, used across all corporate functions? Since there is a methodology for each area, wouldn&apos;t a global one applicable to all areas be better?
          </p>

          <p>
            Our research showed that, once understood, it was possible to identify concepts from each methodology in every use case, regardless of specific area. After justifying the global application of a single methodology, we needed justification for its use. Under the <em>Holistika Corporate Intelligence</em> product, the justification was clear: <strong>Data—intelligence-backed decisions.</strong>
          </p>

          <div className="my-10 p-6 bg-card rounded-xl border border-border">
            <h4 className="text-base font-semibold text-foreground mb-4">The Data Governance Product</h4>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our Data Governance product follows the use of data under these principles. It seeks to integrate the full value of data according to the needs, capabilities, and objectives of any company. Since it is impossible to follow a standard format for the product (the data itself), Holistika solved this by standardizing our methodology.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              It does not matter that each company implements data in a personalized way, because the way Holistika achieves it is standard under our holistic framework. The service is not about creating data, but about <strong>creating a structure that generates it</strong> and proposing strategies on how to use it.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4: The Pincer Effect */}
      <section id="pincer-effect" className="scroll-mt-40 mb-20">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-primary">04</span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">
            The Pincer Effect: Infinite Methodology Development
          </h2>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p>
            The development of methodology is infinite. Holistika&apos;s initial approach was to absorb the largest possible amount of intelligence: investigating all types of companies, products, and industries; analyzing each position with its capabilities and processes; studying all types of use cases and training.
          </p>

          <p>
            After years of this process, we obtained many consistent results, validated by qualified professionals. We reached a conclusion: <strong>the increase in the quality of reusing obtained intelligence far exceeded expected expectations.</strong> This validated packaging all accumulated and categorized frameworks as &ldquo;methodology.&rdquo;
          </p>

          <KeyInsight>
            Methodology is the set of uses, norms, understood variables, standard situations, and logics applied to something that can be exported as a framework directly by processes, projects, or services. At the business level, Holistika&apos;s methodology is an algorithm that simplifies the company&apos;s functions.
          </KeyInsight>

          <p>
            This methodology is why Holistika does not need to specialize only in the services it currently offers—it can delve into any area of business it chooses.
          </p>

          <div className="my-10 p-6 bg-teal-600/5 rounded-xl border border-teal-600/20">
            <h4 className="text-base font-semibold text-accent-primary mb-4">The Pincer Effect Explained</h4>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Data serves as a perfect example. Having already established the strategic, business, tactical, and process foundations, Holistika already has complete higher-level understanding transformed into product (strategy). But the combination of everything through methodology is the Holistika service.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              We approach any area of knowledge and both provide the service and absorb it. Our tactic is to start from the business end <em>and simultaneously</em> from the technical origin level (in our case, coding)—then unite them in understanding how data from its creation applies to business. The pincer effect seems slower initially but follows compound interest, ensuring success as the holistic business vision unites with technical understanding.
            </p>
          </div>
        </div>
      </section>

      {/* Section 5: Susana */}
      <section id="susana" className="scroll-mt-40 mb-20">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-primary">05</span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">
            Susana: Holistika&apos;s Internal AI
          </h2>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p>
            The &ldquo;Susana Manifesto&rdquo; originates from understanding that technology follows a predictable curve. We aim to understand different frameworks and apply them. Our conclusions:
          </p>

          <ul className="list-none space-y-4 my-8">
            <li className="flex gap-4">
              <span className="text-accent-primary font-bold">1.</span>
              <span>Technology appears more technical at the beginning than at the end, which is a problem especially for early adopters. A system that &ldquo;de-technifies&rdquo; is necessary.</span>
            </li>
            <li className="flex gap-4">
              <span className="text-accent-primary font-bold">2.</span>
              <span>Early-stage investments are more accessible due to low competition of projects (initial technological limitation). It is possible to create an interesting project for investment, consumer, investor, and employee—automatically winning in Porter&apos;s five forces because barriers to entry are overcome.</span>
            </li>
          </ul>

          <p>
            Susana aims to create an environment akin to Holistika&apos;s ways of proceeding and processing to offer services that <strong>break the technological barrier of AI.</strong> This means Susana must internally solve the challenge of integrating Holistika&apos;s processes, logic management, and uses into an environment that is easy to manage, adapt, scale, and use.
          </p>

          <div className="my-10 p-6 bg-card rounded-xl border border-border">
            <h4 className="text-base font-semibold text-foreground mb-4">Current Architecture</h4>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2.5" />
                <span>All models centralized in an API repository (Postman) for infrastructure savings and indefinite scaling</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2.5" />
                <span>Agents trained to train others in processes Holistika masters</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2.5" />
                <span>Memory vectorization tools to reduce token consumption while maintaining value</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2.5" />
                <span>Databases interconnected with use cases for controlled token consumption</span>
              </li>
            </ul>
          </div>

          <p>
            Unlike other AGI or AI applications, Susana is the architecture in which users are also supported, so databases are interconnected with use cases, allowing consumption of tokens to be kept under control while constantly updating free use cases.
          </p>
        </div>
      </section>

      {/* Section 6: Conclusion */}
      <section id="conclusion" className="scroll-mt-40 mb-20">
        <div className="flex items-baseline gap-4 mb-6">
          <span className="text-sm font-mono text-accent-primary">06</span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground">
            Conclusion: Management as Technology
          </h2>
        </div>
        
        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
          <p>
            Holistika&apos;s current condition is that it is up-to-date. The same internal Research processes, also used in services, allow it to be at the forefront of the market. The internal use of an architecture of Artificial Intelligences and qualitative database systems exemplifies how we use methodology to incorporate market innovations into our own structure.
          </p>

          <KeyInsight>
            These factors increase efficiency and update services to the most modern standard, always ensuring an innovation factor ahead of competition in strategy services. Innovation behind the company is what ensures the quality of its services.
          </KeyInsight>

          <p>
            As the name suggests, <strong>Holistika Research</strong> provides updated quality knowledge, and the global control necessary to use it.
          </p>

          <div className="my-10 p-8 bg-accent-primary/5 rounded-xl border border-accent-primary/20 text-center">
            <p className="text-xl font-medium text-foreground mb-2">
              Management is a technology.
            </p>
            <p className="text-muted-foreground">
              It can be versioned. It can be scaled. It can be shared.
            </p>
          </div>
        </div>
      </section>

      {/* End Note */}
      <footer className="pt-12 border-t border-border">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <p>Holistika Business Manifiesto</p>
          <p>Version 1.0</p>
        </div>
      </footer>
    </article>
  )
}
